import type Owner from '@ember/owner';
import { tracked } from '@glimmer/tracking';

import type { APIData, GeneralComponentClass } from 'webapp-types';
import { ApiMetricBuilderWizardStep } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import CustomQuerySetupWizardApiMetricBuilderStepDateComponent from 'webapp/components/custom-query/setup/wizard/api-metric-builder/step-date';
import CustomQuerySetupWizardApiMetricBuilderStepDimensionComponent from 'webapp/components/custom-query/setup/wizard/api-metric-builder/step-dimension';
import CustomQuerySetupWizardApiMetricBuilderStepMetricComponent from 'webapp/components/custom-query/setup/wizard/api-metric-builder/step-metric';
import CustomQuerySetupWizardApiMetricBuilderStepMetricDefinitionComponent from 'webapp/components/custom-query/setup/wizard/api-metric-builder/step-metric-definition';
import CustomQuerySetupWizardApiMetricBuilderStepPreviewComponent from 'webapp/components/custom-query/setup/wizard/api-metric-builder/step-preview';
import CustomQuerySetupWizardApiMetricBuilderStepRequestComponent from 'webapp/components/custom-query/setup/wizard/api-metric-builder/step-request';
import type { Args as BaseArgs } from 'webapp/components/custom-query/setup/wizard/base';
import CustomQuerySetupWizardBaseComponent from 'webapp/components/custom-query/setup/wizard/base';

import type CustomMetricSetupFormApiMetricBuilder from 'webapp/models-custom/custom-metric-setup-form/api-metric-builder';

interface Args extends BaseArgs {
	form: CustomMetricSetupFormApiMetricBuilder;
}

export default class CustomQuerySetupWizardApiMetricBuilderComponent extends CustomQuerySetupWizardBaseComponent<Args> {
	@tracked stepId: ApiMetricBuilderWizardStep = ApiMetricBuilderWizardStep.REQUEST;

	wizardType = 'api-metric-builder';

	get allNavigationSteps() {
		const steps: {
			id: ApiMetricBuilderWizardStep;
			text: string;
			component: GeneralComponentClass;
		}[] = [
			{
				id: ApiMetricBuilderWizardStep.REQUEST,
				text: 'API request',
				component: CustomQuerySetupWizardApiMetricBuilderStepRequestComponent,
			},
		];
		if (this.args.form.isNew) {
			steps.push(
				{
					id: ApiMetricBuilderWizardStep.METRIC,
					text: 'Select value',
					component: CustomQuerySetupWizardApiMetricBuilderStepMetricComponent,
				},
				{
					id: ApiMetricBuilderWizardStep.DIMENSION,
					text: 'Segment by dimension',
					component: CustomQuerySetupWizardApiMetricBuilderStepDimensionComponent,
				},
				{
					id: ApiMetricBuilderWizardStep.DATE,
					text: 'Select date',
					component: CustomQuerySetupWizardApiMetricBuilderStepDateComponent,
				},
			);
		}
		steps.push({
			id: ApiMetricBuilderWizardStep.PREVIEW,
			text: 'Preview & customize',
			component: CustomQuerySetupWizardApiMetricBuilderStepPreviewComponent,
		});
		steps.push({
			id: ApiMetricBuilderWizardStep.METRIC_DEFINITION,
			text: 'Describe metric',
			component: CustomQuerySetupWizardApiMetricBuilderStepMetricDefinitionComponent,
		});
		return steps;
	}

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		const sourceType = this.args.model.sourceObj?.sourceType;
		if (sourceType) {
			const trackingData: APIData.Tracking.Data = {
				origin: this.args.origin,
				type: this.args.model.trackingWizardType,
				datasource: sourceType.key,
			};

			if (sourceType.isCustomRESTApi) {
				trackingData.api_url = this.args.form.baseUrl;
			}

			this.analyticsService.event(TrackingEvent.QUERY_CREATE_WIZARD_STARTED, trackingData);
		}
	}

	override get showNavigation(): boolean {
		return this.stepId !== ApiMetricBuilderWizardStep.REQUEST;
	}

	goToPrevOrNextStep(fromStepId: string, isPrev: boolean): void {
		const offset = isPrev ? -1 : 1,
			navigationSteps = this.navigationSteps,
			currentNavigationStep = navigationSteps.find((item) => item.id === fromStepId);

		if (currentNavigationStep) {
			const currentStepIdx = navigationSteps.indexOf(currentNavigationStep),
				prevOrNextStep = navigationSteps[currentStepIdx + offset];

			if (prevOrNextStep) {
				// all good, actually go
				this.goToStep(prevOrNextStep.id);
			} else {
				// no valid step found
				// only handle next - previous step is not possible atm
				if (!isPrev) {
					// next step - query is saved, go forward
					this.args.afterQuerySave();
				}
			}
		}
	}

	selectInitialStep(): void {
		const initialStep = ApiMetricBuilderWizardStep.REQUEST; // default - start
		this.goToStep(initialStep);
	}

	trackWizardStep(): void {
		const pageNameMap = {
			[ApiMetricBuilderWizardStep.REQUEST]: 'Request',
			[ApiMetricBuilderWizardStep.METRIC]: 'Value',
			[ApiMetricBuilderWizardStep.DIMENSION]: 'Dimension Value',
			[ApiMetricBuilderWizardStep.DATE]: 'Date',
			[ApiMetricBuilderWizardStep.PREVIEW]: 'Preview',
			[ApiMetricBuilderWizardStep.METRIC_DEFINITION]: 'Settings',
		};
		// tracking as pageview
		this.analyticsService.page(`Query Wizard ${pageNameMap[this.stepId]}`);
	}
}
